import { Fragment, useState, useEffect } from 'react'
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react'
import {
  Bars3Icon,
  ChartPieIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  HomeIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'
import Providers from './Providers/index'
import Services from './Services/index'
import Manage from './Manage/index'
import TopBar from '../global/TopBar'
import { useSelector } from 'react-redux'
import ListProviders from './Providers/ListProviders'
import ProviderProfile from './Providers/ProviderProfile'

const navigation = [
  { name: 'Search', href: '#', icon: HomeIcon, current: true },
  { name: 'Services', href: '#', icon: DocumentDuplicateIcon, current: false },
  { name: 'Manage', href: '#', icon: ChartPieIcon, current: false },
]

export default function Store() {
  const token = useSelector((state) => state.auth.token)
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [currentItem, setCurrentItem] = useState('Search')
  const user = useSelector((state) => state.auth.user)
  const isServiceProvider = user.isServiceProvider
  const [providers, setProviders] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [selectedProvider, setSelectedProvider] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault()
    if (!searchValue.trim()) return

    setIsLoading(true)
    setProviders([]) // Clear previous results

    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVICE_API_URL}/services?search=${searchValue}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Api-Name': 'service-api',
            'Content-Type': 'application/json',
          }
        }
      )

      if (!response.ok) {
        throw new Error('Search failed')
      }

      const data = await response.json()
      
      // Handle the response data properly
      if (data && Array.isArray(data)) {
        setProviders(data)
      } else if (data && Array.isArray(data.providers)) {
        setProviders(data.providers)
      } else if (data && Array.isArray(data.data)) {
        setProviders(data.data)
      } else {
        setProviders([])
        console.error('Unexpected API response format:', data)
      }
    } catch (error) {
      console.error('Search error:', error)
      setProviders([])
      // You might want to add error state handling here
    } finally {
      setIsLoading(false)
    }
  }

  const handleButtonClick = (name) => {
    setCurrentItem(name)
    setSidebarOpen(false)
    navigation.forEach(item => item.current = item.name === name)
  }

  return (
    <div className="min-h-screen bg-[#1a1f2e]">
      <TopBar />

      <main className="relative">
        {/* Hero Section - Only show when no search results and not loading and no selected provider */}
        <AnimatePresence>
          {!providers.length && !isLoading && !selectedProvider && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="relative px-4 sm:px-6 lg:px-8 pt-24 pb-12 text-center max-w-7xl mx-auto"
            >
              <motion.h1
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-8"
              >
                <span className="inline-block bg-gradient-to-r from-[#FFD700] to-[#90EE90] text-transparent bg-clip-text">
                  Professional Services
                </span>
                <br />
                <span className="inline-block text-[#90EE90] mt-2">
                  at Your Fingertips
                </span>
              </motion.h1>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search Bar - Only show when no provider is selected */}
        {!selectedProvider && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ 
              y: 0, 
              opacity: 1,
              marginTop: providers.length ? '2rem' : '0'
            }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ delay: 0.4 }}
            className="max-w-3xl mx-auto px-4"
          >
            <form onSubmit={handleSearch} className="relative">
              <div className="relative group">
                <input
                  type="text"
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Search for services..."
                  className="w-full h-14 px-6 pr-16 rounded-full bg-white/10 backdrop-blur-xl
                           border border-white/20 text-white placeholder-gray-400
                           focus:outline-none focus:ring-2 focus:ring-yellow-300/50
                           transition-all duration-300"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-2 p-2.5 rounded-full
                           bg-gradient-to-r from-yellow-300 to-green-300
                           text-[#1a1f2e] hover:shadow-lg hover:scale-105
                           transition-all duration-300"
                >
                  <MagnifyingGlassIcon className="w-5 h-5" />
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* Service Provider Controls */}
        {isServiceProvider && !selectedProvider && (
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="fixed top-20 left-4 z-40 p-2.5 rounded-full bg-white/10 
                     backdrop-blur-lg border border-white/20 shadow-lg"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6 text-yellow-300" />
          </motion.button>
        )}

        {/* Sidebar Component remains unchanged */}
        {/* ... existing sidebar code ... */}

        {/* Search Results and Provider Profile */}
        <AnimatePresence mode="wait">
          {providers && providers.length > 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto"
            >
              {selectedProvider ? (
                <ProviderProfile 
                  key="profile"
                  provider={selectedProvider} 
                  onClose={() => setSelectedProvider(null)}
                />
              ) : (
                <motion.div
                  key="results"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <div className="mb-6">
                    <h2 className="text-2xl font-bold text-white">
                      Search Results
                    </h2>
                    <p className="text-gray-400">
                      Found {providers.length} service provider{providers.length !== 1 ? 's' : ''}
                    </p>
                  </div>
                  
                  <ListProviders 
                    providerList={providers} 
                    setSelectedProvider={setSelectedProvider}
                  />
                </motion.div>
              )}
            </motion.div>
          ) : isLoading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-300"></div>
            </div>
          ) : searchValue.trim() !== '' && !isLoading ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12 text-gray-400"
            >
              No results found for "{searchValue}"
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* Main Content Area for other views */}
        {currentItem !== 'Search' && !selectedProvider && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="px-4 sm:px-6 lg:px-8 py-6"
          >
            {currentItem === 'Services' ? <Services /> : 
             currentItem === 'Manage' ? <Manage /> : null}
          </motion.div>
        )}
      </main>
    </div>
  )
}
